import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Chip,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Casino as CasinoIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { sevenUpDownAPI } from '../../../services/api';

const StatCard = ({ title, value, icon, color, subtitle }) => (
  <Card
    sx={{
      background: `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)`,
      border: `1px solid ${color}25`,
      borderRadius: 2,
      height: '100%',
    }}
  >
    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box flex={1}>
          <Typography color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="h6" component="div" sx={{ color, fontWeight: 'bold', fontSize: '1.1rem' }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ color: color, opacity: 0.6 }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const SevenUpDownStatistics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [recentResults, setRecentResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    days: 7,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [analyticsRes, resultsRes] = await Promise.all([
        sevenUpDownAPI.getAdminAnalytics({ days: filters.days }),
        sevenUpDownAPI.getRecentResults({ limit: 20 })
      ]);

      if (analyticsRes.data.success) {
        setAnalytics(analyticsRes.data.analytics);
      }
      if (resultsRes.data.success) {
        setRecentResults(resultsRes.data.results || []);
      }
    } catch (err) {
      console.error('Error fetching statistics:', err);
      toast.error(err.response?.data?.message || 'Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    fetchData();
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getOptionColor = (option) => {
    switch (option) {
      case 'DOWN': return '#f44336';
      case 'SEVEN': return '#9c27b0';
      case 'UP': return '#4caf50';
      default: return '#757575';
    }
  };

  const getOptionEmoji = (option) => {
    switch (option) {
      case 'DOWN': return '⬇️';
      case 'SEVEN': return '7️⃣';
      case 'UP': return '⬆️';
      default: return '❓';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  const gameStats = analytics?.game_stats || {};
  const playerStats = analytics?.player_stats || {};
  const winningDistribution = analytics?.winning_distribution || [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            <AssessmentIcon sx={{ mr: 1, fontSize: 24 }} />
            🎲 7 Up Down Statistics
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Days"
                  type="number"
                  value={filters.days}
                  onChange={(e) => setFilters({ ...filters, days: parseInt(e.target.value) || 7 })}
                  inputProps={{ min: 1, max: 365 }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SearchIcon />}
                  onClick={handleApplyFilters}
                  fullWidth
                >
                  Apply
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Overview Statistics */}
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          Game Overview (Last {filters.days} Days)
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <StatCard
              title="Total Rounds"
              value={parseInt(gameStats.total_rounds || 0).toLocaleString()}
              icon={<CasinoIcon sx={{ fontSize: 28 }} />}
              color="#667eea"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              title="Total Bets"
              value={formatCurrency(gameStats.total_bets)}
              icon={<MoneyIcon sx={{ fontSize: 28 }} />}
              color="#f5576c"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              title="Total Payouts"
              value={formatCurrency(gameStats.total_payouts)}
              icon={<TrendingUpIcon sx={{ fontSize: 28 }} />}
              color="#4facfe"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              title="House Profit"
              value={formatCurrency(gameStats.total_house_profit)}
              icon={parseFloat(gameStats.total_house_profit) >= 0 ? 
                <TrendingUpIcon sx={{ fontSize: 28 }} /> : 
                <TrendingDownIcon sx={{ fontSize: 28 }} />
              }
              color={parseFloat(gameStats.total_house_profit) >= 0 ? '#00c853' : '#ff1744'}
            />
          </Grid>
        </Grid>

        {/* Player Statistics */}
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          Player Activity
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={4}>
            <StatCard
              title="Unique Players"
              value={parseInt(playerStats.unique_players || 0).toLocaleString()}
              icon={<PeopleIcon sx={{ fontSize: 28 }} />}
              color="#9c27b0"
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <StatCard
              title="Total Bets Placed"
              value={parseInt(playerStats.total_bets || 0).toLocaleString()}
              icon={<CasinoIcon sx={{ fontSize: 28 }} />}
              color="#ff9800"
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <StatCard
              title="Avg Bet Amount"
              value={formatCurrency(playerStats.avg_bet_amount)}
              icon={<MoneyIcon sx={{ fontSize: 28 }} />}
              color="#00bcd4"
            />
          </Grid>
        </Grid>

        {/* Winning Distribution */}
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          Winning Option Distribution
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {['DOWN', 'SEVEN', 'UP'].map((option) => {
            const data = winningDistribution.find(w => w.winning_option === option) || { count: 0, total_payout: 0 };
            return (
              <Grid item xs={4} key={option}>
                <Card sx={{ 
                  background: `linear-gradient(135deg, ${getOptionColor(option)}15 0%, ${getOptionColor(option)}08 100%)`,
                  border: `1px solid ${getOptionColor(option)}25`,
                  borderRadius: 2,
                }}>
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="body2" sx={{ color: getOptionColor(option), fontWeight: 600, fontSize: '0.85rem' }}>
                          {getOptionEmoji(option)} {option}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: getOptionColor(option), fontSize: '1.1rem' }}>
                          {parseInt(data.count || 0).toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {formatCurrency(data.total_payout)}
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        fontSize: 20, 
                        opacity: 0.4,
                        color: getOptionColor(option),
                        fontWeight: 'bold'
                      }}>
                        {option === 'DOWN' && '2-6'}
                        {option === 'SEVEN' && '7'}
                        {option === 'UP' && '8-12'}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Recent Results */}
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          Recent Round Results
        </Typography>
        <Card sx={{ borderRadius: 2 }}>
          <TableContainer component={Paper} sx={{ maxHeight: 350 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ py: 1, fontSize: '0.75rem' }}>Round</TableCell>
                  <TableCell align="center" sx={{ py: 1, fontSize: '0.75rem' }}>Dice 1</TableCell>
                  <TableCell align="center" sx={{ py: 1, fontSize: '0.75rem' }}>Dice 2</TableCell>
                  <TableCell align="center" sx={{ py: 1, fontSize: '0.75rem' }}>Total</TableCell>
                  <TableCell align="center" sx={{ py: 1, fontSize: '0.75rem' }}>Result</TableCell>
                  <TableCell align="right" sx={{ py: 1, fontSize: '0.75rem' }}>Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentResults.map((round) => (
                  <TableRow key={round.round_number} hover sx={{ '& td': { py: 0.75 } }}>
                    <TableCell>
                      <Typography variant="caption" fontWeight="bold">
                        #{round.round_number}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="caption">🎲 {round.dice1_value}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="caption">🎲 {round.dice2_value}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        {round.total_dice_sum}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={`${getOptionEmoji(round.winning_option)} ${round.winning_option}`}
                        size="small"
                        sx={{ 
                          backgroundColor: getOptionColor(round.winning_option),
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.7rem',
                          height: 22
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption" color="text.secondary">
                        {round.result_declared_at ? new Date(round.result_declared_at).toLocaleTimeString() : '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                {recentResults.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary" sx={{ py: 2, fontSize: '0.8rem' }}>
                        No results found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>
    </motion.div>
  );
};

export default SevenUpDownStatistics;
